import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, X } from "lucide-react";

const TOUR_STORAGE_KEY = "zindeal.demoTourCompleted.v1";
export const DEMO_TOUR_REPLAY_EVENT = "zindeal:demo-tour-replay";

interface TourStep {
  selector?: string;
  title: string;
  body: string;
  placement?: "right" | "bottom" | "center";
}

const STEPS: TourStep[] = [
  {
    title: "Welcome to the Zindeal sandbox",
    body: "A short tour of the surfaces you can browse in this public preview. Exports, buyout math, and clause methodology stay locked — book a walkthrough for the full product.",
    placement: "center",
  },
  {
    selector: '[data-tour="nav-coaches"]',
    title: "Coaches",
    body: "Profiles for every coach on staff — contract status, insurance, and persona-aware notes all live here.",
    placement: "right",
  },
  {
    selector: '[data-tour="nav-contracts"]',
    title: "Contracts",
    body: "Browse the contract lifecycle: status, negotiations, and the audit trail. Open a contract to see the detail view.",
    placement: "right",
  },
  {
    selector: '[data-tour="nav-renewals"]',
    title: "Renewals",
    body: "Pipeline view of every contract approaching expiry, with lifecycle reminders that fire automatically.",
    placement: "right",
  },
  {
    title: "You're all set",
    body: "Explore freely — sensitive math, downloads, and clause libraries stay locked in the public demo. Data resets nightly. Book a walkthrough when you want the full product.",
    placement: "center",
  },
];

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function readRect(selector: string | undefined): Rect | null {
  if (!selector) return null;
  const el = document.querySelector(selector) as HTMLElement | null;
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function hasCompletedTour(): boolean {
  try {
    return localStorage.getItem(TOUR_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function markTourCompleted() {
  try {
    localStorage.setItem(TOUR_STORAGE_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function DemoTour({ enabled }: { enabled: boolean }) {
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);

  // Auto-start the first time a demo visitor lands on the dashboard.
  useEffect(() => {
    if (!enabled) return;
    if (hasCompletedTour()) return;
    // Slight delay so the sidebar / dashboard finish painting before we
    // try to anchor on their nav items.
    const t = window.setTimeout(() => {
      setStepIdx(0);
      setActive(true);
    }, 600);
    return () => window.clearTimeout(t);
  }, [enabled]);

  // Listen for the replay event from the DemoBanner.
  useEffect(() => {
    if (!enabled) return;
    const handler = () => {
      setStepIdx(0);
      setActive(true);
    };
    window.addEventListener(DEMO_TOUR_REPLAY_EVENT, handler);
    return () => window.removeEventListener(DEMO_TOUR_REPLAY_EVENT, handler);
  }, [enabled]);

  const step = STEPS[stepIdx];

  const recompute = useCallback(() => {
    if (!active || !step) return;
    setRect(readRect(step.selector));
  }, [active, step]);

  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  useEffect(() => {
    if (!active) return;
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    // If the anchor isn't in the DOM yet (e.g. sidebar still mounting), poll
    // briefly until it appears or we give up.
    let tries = 0;
    const interval = window.setInterval(() => {
      if (rect || !step?.selector) {
        window.clearInterval(interval);
        return;
      }
      const next = readRect(step.selector);
      if (next) {
        setRect(next);
        window.clearInterval(interval);
        return;
      }
      tries += 1;
      if (tries > 20) window.clearInterval(interval);
    }, 150);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
      window.clearInterval(interval);
    };
  }, [active, recompute, rect, step?.selector]);

  function next() {
    if (stepIdx >= STEPS.length - 1) {
      finish();
      return;
    }
    setRect(null);
    setStepIdx((i) => i + 1);
  }
  function back() {
    if (stepIdx === 0) return;
    setRect(null);
    setStepIdx((i) => i - 1);
  }
  function finish() {
    markTourCompleted();
    setActive(false);
    setStepIdx(0);
    setRect(null);
  }

  if (!enabled || !active || !step) return null;

  const isCenter = step.placement === "center" || !step.selector;
  const padding = 6;
  const highlightStyle: React.CSSProperties | undefined =
    !isCenter && rect
      ? {
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        }
      : undefined;

  // Position the tooltip card.
  let tipStyle: React.CSSProperties = {};
  if (isCenter) {
    tipStyle = {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      maxWidth: 420,
      width: "calc(100vw - 32px)",
    };
  } else if (rect) {
    const tipWidth = 320;
    const gap = 14;
    if (step.placement === "bottom") {
      tipStyle = {
        top: rect.top + rect.height + gap,
        left: Math.max(16, Math.min(window.innerWidth - tipWidth - 16, rect.left)),
        width: tipWidth,
      };
    } else {
      // default: right of the anchor
      const left = rect.left + rect.width + gap;
      const fitsRight = left + tipWidth + 16 < window.innerWidth;
      tipStyle = {
        top: Math.max(16, rect.top),
        left: fitsRight ? left : Math.max(16, rect.left + rect.width / 2 - tipWidth / 2),
        width: tipWidth,
      };
    }
  } else {
    // Anchor not found — degrade to centered.
    tipStyle = {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      maxWidth: 420,
      width: "calc(100vw - 32px)",
    };
  }

  return createPortal(
    <div
      data-testid="demo-tour"
      data-tour-step={stepIdx}
      className="fixed inset-0 z-[100] pointer-events-none"
      aria-live="polite"
    >
      {/* Dim layer */}
      <div className="absolute inset-0 bg-black/55 pointer-events-auto" onClick={() => { /* swallow */ }} />
      {/* Highlight cutout */}
      {highlightStyle && (
        <div
          className="absolute rounded-lg ring-2 ring-indigo-400 ring-offset-2 ring-offset-transparent transition-all duration-200 pointer-events-none"
          style={{
            ...highlightStyle,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
          }}
        />
      )}
      {/* Tooltip */}
      <div
        ref={tipRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-tour-title"
        className="absolute pointer-events-auto rounded-xl border border-border bg-background shadow-2xl p-5 max-w-[420px]"
        style={tipStyle}
        data-testid="demo-tour-card"
      >
        <div className="flex items-start gap-3">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
            <Sparkles className="h-4 w-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 id="demo-tour-title" className="text-sm font-semibold leading-tight">
                {step.title}
              </h3>
              <button
                type="button"
                onClick={finish}
                aria-label="Skip tour"
                className="text-muted-foreground hover:text-foreground"
                data-testid="demo-tour-skip"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
            <div className="mt-4 flex items-center justify-between gap-3">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Step {stepIdx + 1} of {STEPS.length}
              </span>
              <div className="flex gap-2">
                {stepIdx > 0 && (
                  <Button size="sm" variant="ghost" onClick={back} data-testid="demo-tour-back">
                    Back
                  </Button>
                )}
                <Button size="sm" onClick={next} data-testid="demo-tour-next">
                  {stepIdx === STEPS.length - 1 ? "Done" : "Next"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
